/* eslint-disable camelcase */
import {
    TrackingContact,
    TrackingError,
    TrackingInformation,
    TrackingPageView,
} from '../interfaces/tracking';
import { getCMSConfig } from './cms';
import {
    trackIt,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_INFORMATION_TRIGGER_ACCORDION,
    TRACKING_TYPE_INFORMATION,
    TRACKING_TYPE_PAGEVIEW,
    TRACKING_TYPE_SWITCH_OPTIONS,
    TRACKING_ERROR_TYPE_TECHNICAL,
    TRACKING_TYPE_ERROR,
    TRACKING_ERROR_TYPE_VALIDATION,
} from '@vfde-sails/tracking';
import {
    TRACKING_CAPTCHA_SHOWN,
    TRACKING_CONTACT_ACTION_ENGAGE,
    TRACKING_CONTACT_SUBJECT_FIBER,
    TRACKING_CONTACT_TOOL,
    TRACKING_CONTACT_TRIGGER_AUTO,
    TRACKING_CONTACT_TRIGGER_BUTTON,
    TRACKING_ERROR_ACTION,
    TRACKING_INFORMATION_TRIGGER_ICON,
    TRACKING_INFORMATION_TRIGGER_REFERENCE,
    TRACKING_INFORMATION_UI_TYPE_FOOTNOTE,
    TRACKING_INFORMATION_UI_TYPE_TOOLTIP,
    TRACKING_TYPE_CONTACT,
} from '../constants';
import { formatToTrackingString } from './formatter';
import { debugInfo } from './debug';
import { TrackingAvcStep } from '../enums/tracking';
import { TOOLTIP_CLASSNAME_TRIGGER_ELEMENT } from '@vfde-brix/ws10/tooltip';
import {
    getAVCPageView,
    getOptionByMetacode,
    getGeneralPageView,
    getTrackingOrder,
} from './trackingHelper';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { AvcResult, JarvisProduct, TivanoData } from '@vfde-sails/avc-core';
import { AccordionType } from '../components/accordion';

const trackTooltip = function (this: HTMLElement) {
    const tooltipText = this.parentElement?.innerText || '';

    const payload: TrackingInformation = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(tooltipText),
        information_ui_type: TRACKING_INFORMATION_UI_TYPE_TOOLTIP,
        information_trigger: TRACKING_INFORMATION_TRIGGER_ICON,
    };

    debugInfo('Track tooltip: ', payload);
    trackIt(TRACKING_TYPE_INFORMATION, payload);
};

export const addTooltipTracking = (element: Element | string) => {
    const tooltip = element instanceof Element ? element : document.getElementById(element || '');

    const [tooltipIcon] = tooltip?.parentElement?.getElementsByClassName(
        TOOLTIP_CLASSNAME_TRIGGER_ELEMENT
    ) || [];

    tooltipIcon?.removeEventListener('mouseover', trackTooltip);
    tooltipIcon?.addEventListener('mouseover', trackTooltip, { once: true });
};

export const trackAccordion = (text: string, type: AccordionType) => {
    const payload: TrackingInformation = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(text),
        information_ui_type: type,
        information_trigger: TRACKING_INFORMATION_TRIGGER_ACCORDION,
    };

    debugInfo('Track accordion: ', payload);
    trackIt(TRACKING_TYPE_INFORMATION, payload);
};

export const trackGigakombi = (selectedTariff: JarvisProduct) => {
    if (!selectedTariff) { return; }

    const config = getCMSConfig()?.visibleDiscountMetacodes;

    const payload = {
        selectedOption: [
            getOptionByMetacode(selectedTariff, config?.gigakombi, true),
        ],
    };

    debugInfo('Track switcher event: ', payload);
    trackIt(TRACKING_TYPE_SWITCH_OPTIONS, payload);
};

export const trackPageView = (
    selectedTariff: JarvisProduct,
    gigakombi: boolean,
    fnf: boolean,
    avcResult?: AvcResult,
    avcStep?: TrackingAvcStep,
    isAutoOverlayOpened?: boolean,
    accessSupplier?: string,
    availabilityIndicator?: string,
    tivano?: TivanoData,
) => {
    if (!selectedTariff) { return; }
    setTimeout(() => {
        const hotlineNumbers = (window as any)?.VF['new-flyout']?.hotlineNumbers;

        const payload: TrackingPageView = {
            ...getGeneralPageView(),
            ...avcStep
                ? getAVCPageView(selectedTariff, avcResult, avcStep, accessSupplier, availabilityIndicator,
                    tivano)
                : { oOrder: getTrackingOrder(selectedTariff, gigakombi, fnf) },
            ...hotlineNumbers && { hotline_numbers: hotlineNumbers },
        };

        debugInfo('Track pageview event: ', payload);
        trackIt(TRACKING_TYPE_PAGEVIEW, payload);
    }, isAutoOverlayOpened ? 5000 : 0);
};

export const trackLMFormOpen = () => {
    const payload: TrackingContact = {
        contact_action: TRACKING_INFORMATION_ACTION_REVEAL,
        contact_subject: TRACKING_CONTACT_SUBJECT_FIBER,
        contact_tool: TRACKING_CONTACT_TOOL,
        contact_trigger: TRACKING_CONTACT_TRIGGER_BUTTON,
    };

    debugInfo('Track contact open event: ', payload);
    trackIt(TRACKING_TYPE_CONTACT, payload);
};

export const trackLMFormSubmit = () => {
    const payload: TrackingContact = {
        contact_action: TRACKING_CONTACT_ACTION_ENGAGE,
        contact_subject: TRACKING_CONTACT_SUBJECT_FIBER,
        contact_tool: TRACKING_CONTACT_TOOL,
        contact_trigger: TRACKING_CONTACT_TRIGGER_AUTO,
    };

    debugInfo('Track contact submit event: ', payload);
    trackIt(TRACKING_TYPE_CONTACT, payload);
};

export const trackFootnoteLink = (text: string) => {
    const payload = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(text),
        information_ui_type: TRACKING_INFORMATION_UI_TYPE_FOOTNOTE,
        information_trigger: TRACKING_INFORMATION_TRIGGER_REFERENCE,
    };

    debugInfo('Track footnote link: ', payload);
    trackIt(TRACKING_TYPE_INFORMATION, payload);
};

export const trackError = (
    error: Error | ApiErrorResponseWithCode,
    isValidationError: boolean = false
) => {
    const payload: TrackingError = {
        error_action: TRACKING_ERROR_ACTION,
        error_type: isValidationError ? TRACKING_ERROR_TYPE_VALIDATION : TRACKING_ERROR_TYPE_TECHNICAL,
        error_message: formatToTrackingString(error.message),
        ...'status' in error && { error_code: error.status.toString() },
    };

    debugInfo('Track error event: ', payload);
    trackIt(TRACKING_TYPE_ERROR, payload);
};

export const trackCaptcha = () => {
    debugInfo('Captcha tracking: ', TRACKING_CAPTCHA_SHOWN);
    trackIt(TRACKING_CAPTCHA_SHOWN, {});
};
